import { BitmapCoordinatesRenderingScope } from 'fancy-canvas'
import DrawingBase from '../drawing.base'
import {
    ColorValue,
    DrawingSettingGroup,
    DrawingTypes,
    LabelAxisViewConfig,
    Point,
    PointCoordinates,
    SettingValues
} from '../drawing.interface'
import { PrimitiveHoveredItem, Time } from 'lightweight-charts'
import { SettingType } from '../drawing.enums'
import { getDistanceFromLine, scaledCoordinate } from '../drawing.helper'

export type ThreeDivesPatternState = {
    a?: Point,
    b?: Point,
    c?: Point,
    d?: Point,
    e?: Point,
    f?: Point,
    g?: Point,
}

export type ThreeDivesPatternSettings = {
    lineColor: ColorValue,
    lineWidth: number
}

const THRESHOLD = 10 // cursor distance threshold for treating as hovered or not

class ThreeDivesPatternTool extends DrawingBase<any, ThreeDivesPatternSettings, ThreeDivesPatternState> {

    scaledCoordinates: Record<string, PointCoordinates> = {}
    lengths: Record<string, number> = {}
    slopes: Record<string, number> = {}
    intercepts: Record<string, number> = {}

    draw(scope: BitmapCoordinatesRenderingScope) {
        const ctx = scope.context
        this.calculateLineAttributes()
        const { a, b, c, d, e, f, g } = this.scaledCoordinates

        let prevPoint: PointCoordinates

        ctx.beginPath()
        this.setLineAttributes(ctx)
        const points = [a, b, c, d, e, f, g]
        points.forEach((p, i) => {
            if (!p) return

            if (!prevPoint) {
                ctx.moveTo(p.x, p.y)
            } else {
                ctx.lineTo(p.x, p.y)
            }
            prevPoint = p
        })

        ctx.globalAlpha = this._settings.lineColor.opacity
        ctx.stroke()

        ctx.closePath()
        this.drawChangeLines(ctx)

    };

    drawChangeLines(ctx: CanvasRenderingContext2D) {
        const { a, b, c, d, e, f } = this.scaledCoordinates
        ctx.beginPath()

        if (d) {
            ctx.moveTo(b.x, b.y)
            ctx.lineTo(d.x, d.y)
        }

        if (f) {
            ctx.moveTo(d.x, d.y)
            ctx.lineTo(f.x, f.y)
        }

        this.setLineAttributes(ctx, true)
        ctx.stroke()
        ctx.closePath()
        this.drawChangeRatios(ctx)
    }

    drawChangeRatios(ctx: CanvasRenderingContext2D) {
        const { bc, cd, de, ef } = this.lengths
        const { b, d, f } = this.scaledCoordinates

        if (d) {
            const ratio = Number((cd / bc).toFixed(3))
            this.drawRatioText(ctx, ratio.toString(), b, d)
        }

        if (f) {
            let ratio = Number((ef / de).toFixed(3))
            this.drawRatioText(ctx, ratio.toString(), d, f)
        }

    }

    drawRatioText(ctx: CanvasRenderingContext2D, text: string, start: PointCoordinates, end: PointCoordinates) {
        ctx.font = (12 * this.verticalPixelRatio) + 'px sans-serif'
        ctx.textBaseline = 'top'
        ctx.globalAlpha = 1
        const padding = 4 * window.devicePixelRatio
        const dim = ctx.measureText(text)
        const textWidth = dim.width
        const textHeight = dim.actualBoundingBoxAscent + dim.actualBoundingBoxDescent
        const centerX = (start.x + end.x) / 2
        const centerY = (start.y + end.y) / 2
        const startX = centerX - textWidth / 2
        const startY = centerY - textHeight
        ctx.fillStyle = this._settings.lineColor.color
        ctx.beginPath()
        ctx.roundRect(startX - padding, startY - padding, textWidth + 2 * padding, textHeight + 2 * padding + 2 * window.devicePixelRatio, 3 * this.verticalPixelRatio)
        ctx.fill()
        ctx.closePath()
        ctx.fillStyle = '#fff'
        ctx.fillText(text, startX, startY)
    }

    calculateLineAttributes() {
        const { a, b, c, d, e, f, g } = this._state

        let prevPoint: Point;
        [a, b, c, d, e, f, g].forEach((p) => {
            if (!p) return
            let x = this.timeToCoordinate(p.x)
            let y = this.priceToCoordinate(p.y)
            let currentCoordinates: PointCoordinates = { x: 0, y: 0 }
            if (x && y) {
                currentCoordinates = {
                    x: scaledCoordinate(x, this.horizontalPixelRatio),
                    y: scaledCoordinate(y, this.verticalPixelRatio)
                }

                this.scaledCoordinates[p.id] = currentCoordinates

                if (prevPoint) {
                    const key = `${prevPoint.id}${p.id}`
                    this.lengths[key] = Math.abs(p.y - prevPoint.y)
                    const prevCoords = this.scaledCoordinates[prevPoint.id]
                    const slope = (currentCoordinates.y - prevCoords.y) / (currentCoordinates.x - prevCoords.x)
                    const intercept = currentCoordinates.y - slope * currentCoordinates.x
                    this.slopes[key] = slope
                    this.intercepts[key] = intercept
                }
            }
            prevPoint = p
        })
    }

    setLineAttributes(ctx: CanvasRenderingContext2D, setLineType = false) {
        const lineWidth = this._settings.lineWidth * window.devicePixelRatio
        if (setLineType) {
            const dotWidth = Math.max(2, this._settings.lineWidth) * window.devicePixelRatio
            let lineDash: number[] = [dotWidth * 2, dotWidth]
            ctx.setLineDash(lineDash)
        }
        ctx.globalAlpha = this._settings.lineColor.opacity
        ctx.lineWidth = lineWidth
        ctx.strokeStyle = this._settings.lineColor.color
    }

    onAddPoint = (time: Time, price: number, coordinates: PointCoordinates, preview?: boolean) => {
        const point: Point = {
            id: 'a',
            x: time,
            y: price
        }

        if (!this.lastAddedPoint) {
            this._state.a = point
        } else if (this.lastAddedPoint.id == 'a') {
            point.id = 'b'
            this._state.b = point
        } else if (this.lastAddedPoint.id == 'b') {
            point.id = 'c'
            this._state.c = point
        } else if (this.lastAddedPoint.id == 'c') {
            point.id = 'd'
            this._state.d = point
        } else if (this.lastAddedPoint.id == 'd') {
            point.id = 'e'
            this._state.e = point
        } else if (this.lastAddedPoint.id == 'e') {
            point.id = 'f'
            this._state.f = point
        } else {
            point.id = 'g'
            this._state.g = point
            !preview && this.onDrawingEnd()
        }

        if (!preview) return { ...point }
    }

    getHandles = (): Point[] => {
        const { a, b, c, d, e, f, g } = this._state

        return [a!, b!, c!, d!, e!, f!, g!].filter(p => p)
    }

    hitTest(x: number, y: number): PrimitiveHoveredItem | null {
        const scaledX = scaledCoordinate(x, this.horizontalPixelRatio)
        const scaledY = scaledCoordinate(y, this.verticalPixelRatio)
        const { a, b, c, d, e, f, g } = this.scaledCoordinates
        if (!a || !b || !c || !d || !e || !f || !g) return null

        const d1 = getDistanceFromLine(scaledX, scaledY, this.slopes.ab, this.intercepts.ab)
        const d2 = getDistanceFromLine(scaledX, scaledY, this.slopes.bc, this.intercepts.bc)
        const d3 = getDistanceFromLine(scaledX, scaledY, this.slopes.cd, this.intercepts.cd)
        const d4 = getDistanceFromLine(scaledX, scaledY, this.slopes.de, this.intercepts.de)
        const d5 = getDistanceFromLine(scaledX, scaledY, this.slopes.ef, this.intercepts.ef)
        const d6 = getDistanceFromLine(scaledX, scaledY, this.slopes.fg, this.intercepts.fg)
        if (d1 <= THRESHOLD || d2 <= THRESHOLD || d3 <= THRESHOLD || d4 <= THRESHOLD || d5 <= THRESHOLD || d6 <= THRESHOLD) {
            return {
                externalId: this.id,
                zOrder: 'normal'
            }
        }

        return null
    }

    updateHandles(points: Point[]) {
        points.forEach((h) => {
            switch (h.id) {
                case 'a':
                    this._state.a = h
                    break
                case 'b':
                    this._state.b = h
                    break
                case 'c':
                    this._state.c = h
                    break
                case 'd':
                    this._state.d = h
                    break
                case 'e':
                    this._state.e = h
                    break
                case 'f':
                    this._state.f = h
                    break
                case 'g':
                    this._state.g = h
                    break
            }
        })
    }

    updateHandle = (id: string, time: Time, price: number) => {
        if (!this._state[id as keyof typeof this._state]) return
        this._state[id as keyof typeof this._state]!.x = time
        this._state[id as keyof typeof this._state]!.y = price
    }
    getPriceAxisLabelPoints = (): LabelAxisViewConfig<number>[] => {
        const { a, b, c, d, e, f, g } = this._state
        const points = [a, b, c, d, e, f, g].filter(v => v)
        if (points.length < 2) return []

        const sorted = [...points].sort((a, b) => a!.y - b!.y)
        const configs: LabelAxisViewConfig<number>[] = [
            {
                id: 'outer',
                p1: sorted[0]!.y,
                p2: sorted[sorted.length - 1]!.y,
                fill: true,
                fillColor: this._settings.lineColor.color,
                labelColors: {
                    p1: this._settings.lineColor.color,
                    p2: this._settings.lineColor.color
                }
            }
        ]

        for (let c = 1; c < sorted.length - 1; c++) {
            configs.push({
                id: 'price-' + sorted[c]!.id,
                p1: sorted[c]!.y,
                p2: sorted[c]!.y,
                fill: false,
                fillColor: this._settings.lineColor.color,
                labelColors: {
                    p1: this._settings.lineColor.color,
                    p2: this._settings.lineColor.color
                }
            })
        }
        return configs
    }

    getTimeAxisLabelPoints = (): LabelAxisViewConfig<Time>[] => {
        const { a, b, c, d, e, f, g } = this._state
        const points = [a, b, c, d, e, f, g].filter(v => v)
        if (points.length < 2) return []
        if (points.length < 2) return []

        const sorted = [...points].sort((a, b) => (a!.x as number) - (b!.x as number))
        const configs: LabelAxisViewConfig<Time>[] = [
            {
                id: 'time',
                p1: sorted[0]!.x,
                p2: sorted[sorted.length - 1]!.x,
                fill: true,
                fillColor: this._settings.lineColor.color,
                labelColors: {
                    p1: this._settings.lineColor.color,
                    p2: this._settings.lineColor.color
                }
            }
        ]

        for (let c = 1; c < sorted.length - 1; c++) {
            configs.push({
                id: 'time-' + sorted[c]!.id,
                p1: sorted[c]!.x,
                p2: sorted[c]!.x,
                fill: false,
                fillColor: this._settings.lineColor.color,
                labelColors: {
                    p1: this._settings.lineColor.color,
                    p2: this._settings.lineColor.color
                }
            })
        }

        return configs
    }

    getAvailableSettings = (): DrawingSettingGroup<ThreeDivesPatternSettings, SettingValues>[] => {
        return [{
            id: 'style',
            displayName: 'Style',
            visible: true,
            settings: [
                {
                    name: 'lineColor',
                    label: 'Line Color',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: '#673ab7',
                        opacity: 1
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'lineWidth',
                    label: 'Line Width',
                    type: SettingType.STROKE_WIDTH,
                    defaultValue: 1,
                    custom: true,
                    visible: true
                }
            ]
        }]
    }

    getSettingsValues(): ThreeDivesPatternSettings {
        return { ...this._settings }
    }

    getDrawingType(): DrawingTypes {
        return '3DivesPattern'
    }

    getMiniSettings = () => {
        return ['lineColor', 'lineWidth'] as (keyof ThreeDivesPatternSettings)[]
    }
}

export default ThreeDivesPatternTool